import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {RequestACallVerificationCodeEndpointResponseClientError} from '../../../../generated/types';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {ConfigContext} from '../../../Providers/ConfigProvider';
import {useApi} from '../../../Providers/ApiProvider/ApiProvider';

type VerificationCodes = {
  agentCode: string;
  userCode: string;
};

// Fetches verification codes and schedules refreshes based on the next reload time
export const useRacVerificationCodes = () => {
  const {audience} = useContext(ConfigContext);
  const [verificationCodes, setVerificationCodes] =
    useState<VerificationCodes>();
  const currTimeout = useRef<NodeJS.Timeout>();
  const [loading, setLoading] = useState<boolean>(false);
  const [clientError, setClientError] =
    useState<RequestACallVerificationCodeEndpointResponseClientError>();
  const [serverError, setServerError] = useState<boolean>(false);

  const analytics = useClientAnalytics();

  const {fetchVerificationCodes} = useApi();

  const fetchCodes = useCallback(async () => {
    analytics.track('support_conversations_rac_pins_refresh');

    setLoading(true);
    fetchVerificationCodes(audience)
      .then((resp) => {
        if (resp.type === 'success') {
          const nextReloadInSec = resp.nextReload;
          currTimeout.current && clearTimeout(currTimeout.current);
          currTimeout.current = setTimeout(fetchCodes, 1000 * nextReloadInSec);
          setVerificationCodes({
            agentCode: resp.agentCode,
            userCode: resp.userCode,
          });
        } else {
          setClientError(resp);
        }
      })
      .catch(() => {
        setServerError(true);
      })
      .finally(() => setLoading(false));
  }, [audience, analytics, fetchVerificationCodes]);

  useEffect(() => {
    if (!verificationCodes && !loading && !clientError && !serverError) {
      fetchCodes();
    }

    return () => {
      currTimeout.current && clearTimeout(currTimeout.current);
    };
  }, [loading, fetchCodes, verificationCodes, clientError, serverError]);

  return {clientError, serverError, loading, verificationCodes};
};
